// ./src/utils/guestOverrides.ts
//
// A guest's name and contact are its PERSON's (owner, 2026-11-02). A value on the guest row is an override a host set for this
// one event, and it wins everywhere, emails included, so the screen has to say when one is active and let the host clear it.
// ⓘ Pure, so `check-guest-overrides` RUNS it.
export const GUEST_OVERRIDE_FIELDS = ["firstName", "lastName", "email", "phone"] as const;

export type GuestOverrideField = (typeof GUEST_OVERRIDE_FIELDS)[number];

type Contactish = Partial<Record<GuestOverrideField, string | null | undefined>>;

const clean = (value: string | null | undefined): string => (value ?? "").trim();

/** The fields where this guest really differs from its contact. A value identical to the contact's is a copy, not an override. */
export const activeGuestOverrides = (guest: Contactish | null | undefined, person: Contactish | null | undefined): GuestOverrideField[] =>
  GUEST_OVERRIDE_FIELDS.filter((field) => {
    const own = clean(guest?.[field]);
    const theirs = clean(person?.[field]);

    return !!own && (field === "email" ? own.toLowerCase() !== theirs.toLowerCase() : own !== theirs);
  });

/**
 * What a save of a LINKED guest sends: only the fields the host changed, an emptied one as null (back to the contact's).
 * ⚠ Never the whole form: a crew member's row is read back with their user's name in these fields, and saving an unrelated
 * edit would write that onto the row as an override.
 */
export const guestOverridePatch = (form: Contactish, guest: Contactish | null | undefined): Partial<Record<GuestOverrideField, string | null>> => {
  const patch: Partial<Record<GuestOverrideField, string | null>> = {};

  for (const field of GUEST_OVERRIDE_FIELDS) {
    const next = clean(form[field]);

    if (next !== clean(guest?.[field])) {
      patch[field] = next || null;
    }
  }

  return patch;
};
